import { ReactNode } from "react";
import { GetProfileQueryQuery } from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";

import {
  ProfileActionItems,
  ProfileFeedback,
  ProfileGoals,
  ProfileRecognitions,
} from "./profile-artifacts";
import ProfileReviews from "./profile-reviews";

export type ProfileContentTab = {
  value: string;
  label: string;
  available: boolean;
  render: () => ReactNode;
};

// The profile "content" tabs (action items, goals, feedback, recognitions,
// reviews) shown behind the profile-content-tabs flag. Centralizes which
// sections exist (org feature flags + permissions) and how to render each, so
// profile-page.tsx can build both the tab buttons and their routes from one
// source of truth.
const useProfileContentTabs = (
  profile: GetProfileQueryQuery["user"] | undefined,
): ProfileContentTab[] => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const currentUser = currentUserVar();
  const isAdmin = isAdminVar();

  if (!profile) return [];

  const isManager = profile.managersList.some(
    ({ id }) => id === currentUser.id,
  );

  return [
    {
      value: "action-items",
      label: label("action item", { titleCase: true, pluralize: true }),
      available: !!organization.featureFlags.actionItems,
      render: () => <ProfileActionItems profile={profile} bare />,
    },
    {
      value: "goals",
      label: label("goal", { titleCase: true, pluralize: true }),
      available: !!organization.featureFlags.goals,
      render: () => <ProfileGoals profile={profile} bare />,
    },
    {
      value: "feedback",
      label: label("feedback", { titleCase: true, pluralize: true }),
      available: !!organization.featureFlags.feedbacks,
      render: () => <ProfileFeedback profile={profile} bare />,
    },
    {
      value: "recognitions",
      label: label("recognition", { titleCase: true, pluralize: true }),
      available: !!organization.featureFlags.recognitions,
      render: () => <ProfileRecognitions profile={profile} bare />,
    },
    {
      value: "reviews",
      label: label("review", { titleCase: true, pluralize: true }),
      available: isAdmin || isManager || profile.id === currentUser.id,
      render: () => <ProfileReviews profileId={profile.id} bare />,
    },
  ];
};

export default useProfileContentTabs;
